import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { attributeService } from '../../services/attributeService';
import { AttributeDefinition } from '../../types';
import { parseApiError } from '../../utils/errorUtils';
import './ProductCategoryListPage.css';
import { CirclePlus, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

/**
 * User-created (custom) product attributes. Module-seeded attributes are
 * platform-managed and never appear here — they always render in the product
 * form's module block.
 */
const AttributeListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [items, setItems] = useState<AttributeDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await attributeService.getDefinitions(true);
      setItems((response.data.data as AttributeDefinition[]) ?? []);
    } catch (err: unknown) {
      setError(parseApiError(err, t('attribute.failedToLoad')).message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await attributeService.getDefinitions(true);
      setItems((response.data.data as AttributeDefinition[]) ?? []);
    } catch (err: unknown) {
      setError(parseApiError(err, t('attribute.failedToLoad')).message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async (item: AttributeDefinition) => {
    if (!window.confirm(t('attribute.confirmDelete', { name: item.name }))) return;
    setError(null);
    try {
      await attributeService.deleteDefinition(item.id);
      setMessage(t('attribute.deleteSuccess'));
      await loadItems();
    } catch (err: unknown) {
      setError(parseApiError(err, t('attribute.failedToDelete')).message);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'name',
      label: t('attribute.name'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'code',
      label: t('attribute.code'),
      sortable: true,
      width: '140px',
      render: (value) => <span className="description-text">{value}</span>,
    },
    {
      key: 'dataType',
      label: t('attribute.dataType'),
      sortable: true,
      width: '110px',
    },
    {
      key: 'appliesToAllProducts',
      label: t('attribute.appliesTo'),
      sortable: false,
      width: '220px',
      hideMobile: true,
      render: (_value, item: AttributeDefinition) =>
        item.appliesToAllProducts
          ? <span>{t('attribute.scopeAllProducts')}</span>
          : <span className="description-text">{item.categoryNames.join(', ') || '—'}</span>,
    },
    {
      key: 'isRequired',
      label: t('attribute.required'),
      sortable: true,
      width: '100px',
      hideMobile: true,
      render: (value) => (value ? t('common.yes') : t('common.no')),
    },
    {
      key: 'isActive',
      label: t('common.status'),
      sortable: true,
      width: '100px',
      render: (value) => (
        <span className="info-badge" style={{ backgroundColor: value ? '#28a745' : '#6c757d' }}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item) => navigate(`/products/attributes/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      onClick: (item) => handleDelete(item as AttributeDefinition),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('attribute.title')}</h1>
          <div className="header-actions">
            <button
              className="btn-new"
              onClick={() => navigate('/products/attributes/create')}
              title={t('attribute.newAttribute')}
            >
              <CirclePlus />
              {t('attribute.newAttribute')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        {message && (
          <div className="alert alert-success alert-dismissible fade show">
            {message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('attribute.noAttributesFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default AttributeListPage;
